import * as store from "../state/store.js";
import { loadDxfFromString } from "./io.js";
import { toast } from "./ui.js";

// POSTs the currently-loaded DXF to /heal (Vite proxies to heal_server.py in
// dev; the Rack host answers directly in prod), then feeds the cleaned bytes
// back through loadDxfFromString so bounds refit, sidebar reopens, and the
// DXF_HEALED custom var on the response is picked up.
//
// Called automatically by loadDxfFromString on any non-healed load. Uses
// store.originalDxf() (the source bytes) rather than re-serialising through
// the exporter — the healing pipeline expects the exact source so unit
// detection and entity-type coverage aren't reduced by our writer's subset.
export async function healCurrentDxf(): Promise<boolean> {
  const content = store.originalDxf();
  if (!content) return false;
  if (store.isHealed.value) return false;
  const filename = store.filename() ?? "drawing.dxf";
  // healActive drives the canvas overlay's progress ring. /heal is a single
  // blocking POST with no streaming checkpoints, so healProgress stays at 0
  // and the ring renders as an indeterminate spinner.
  store.batch(() => {
    store.write.healActive(true);
    store.write.healProgress(0);
  });
  try {
    const res = await fetch("/heal", {
      method: "POST",
      headers: { "Content-Type": "application/octet-stream" },
      body: content,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      toast(`Heal failed (${res.status}${text ? `: ${text.slice(0, 80)}` : ""})`);
      return false;
    }
    const healed = await res.text();
    // Route back through the normal load path so bounds refit, sidebar
    // opens, dxf:load fires with `healed: true`, and DXF_HEALED sticks. The
    // recursive call sees isHealed=true and skips its own auto-heal branch.
    loadDxfFromString(healed, healedFilename(filename));
    toast("Healing complete ✨");
    return true;
  } catch (err) {
    toast(`Heal failed (${err instanceof Error ? err.message : "network error"})`);
    return false;
  } finally {
    store.batch(() => {
      store.write.healActive(false);
      store.write.healProgress(0);
    });
  }
}

function healedFilename(name: string): string {
  const dot = name.lastIndexOf(".");
  if (dot <= 0) return `${name}_healed`;
  return `${name.slice(0, dot)}_healed${name.slice(dot)}`;
}
